import { Option } from '~/components/SmartSelect';
import countries from '~/constants/countries';
import { currencies, Currency } from '~/constants/currency';
import { WeightUnit } from '~/constants/weight';
import * as moment from 'moment';
import { Country } from './Address';
import { JSONCustomsItemPayload } from './CustomsItem';

type JSONLineItem = {
  object_id: string;
  id: number;
  product: string;
  variant: string;
  app_id: string;
  title: string;
  sku: string;
  // TODO: Revisit
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parcel?: any;
  quantity: number;
  is_fulfilled: boolean;
  total_amount: string;
  unit_amount: string;
  currency: Maybe<string>;
  total_weight: string;
  unit_weight: string;
  weight_unit: Maybe<string>;
  manufacture_country: Maybe<string>; // deprecated
  max_ship_time: Maybe<string>;
  max_delivery_time: Maybe<string>;
  hs_code: Maybe<string>;
  variant_title: string;
  country_of_origin: Maybe<string>;
};

type LineItem = {
  objectId: string;
  id: number;
  product: string;
  variant: string;
  appId: string;
  title: string;
  sku: string;
  // TODO: Revisit
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parcel?: any;
  quantity: number;
  isFulfilled: boolean;
  totalAmount: string;
  unitAmount: string;
  currency: Currency;
  totalWeight: string;
  unitWeight: string;
  weightUnit: Maybe<WeightUnit>;
  manufactureCountry: Maybe<string>; // deprecated
  maxShipTime: Maybe<moment.Moment>;
  maxDeliveryTime: Maybe<moment.Moment>;
  hsCode: Maybe<string>;
  variantTitle: string;
  countryOfOrigin: Maybe<Country>;
};

type LineItemPayload = {
  title: string;
  quantity: string;
  unitWeight: string;
  weightUnit: Option;
  unitAmount: string;
  currency: Option;
  countryOfOrigin: Country;
  sku: string;
};

type JSONLineItemPayload = {
  title: string;
  quantity: number;
  unit_weight: string;
  weight_unit: string;
  unit_amount: string;
  currency: string;
  country_of_origin: string;
  sku: string;
};

export type WebLineItem = {
  country_of_origin: string;
  currency: string;
  quantity: number;
  sku: string;
  title: string;
  unit_amount: string;
  unit_weight: string;
  weight_unit: WeightUnit;
  object_id: string;
  is_fulfilled: boolean;
  total_amount: string;
  total_weight: string;
  variant: string;
  variant_title: string;
};

export type WebShipmentLineItemsInner = {
  object_id: string;
  quantity: number;
};

function fromJSON(jsonLineItem: JSONLineItem): LineItem {
  return {
    objectId: jsonLineItem.object_id,
    id: jsonLineItem.id,
    product: jsonLineItem.product,
    variant: jsonLineItem.variant,
    appId: jsonLineItem.app_id,
    title: jsonLineItem.title,
    sku: jsonLineItem.sku,
    parcel: jsonLineItem.parcel,
    quantity: jsonLineItem.quantity,
    isFulfilled: jsonLineItem.is_fulfilled,
    totalAmount: jsonLineItem.total_amount,
    unitAmount: jsonLineItem.unit_amount,
    currency: jsonLineItem.currency
      ? Object.values(currencies).find(
          (currency) => currency.iso === jsonLineItem.currency!.toUpperCase(),
        ) || currencies.USD
      : currencies.USD,
    totalWeight: jsonLineItem.total_weight,
    unitWeight: jsonLineItem.unit_weight,
    weightUnit: jsonLineItem.weight_unit ? <WeightUnit>jsonLineItem.weight_unit : null,
    manufactureCountry: jsonLineItem.manufacture_country,
    maxShipTime: jsonLineItem.max_ship_time ? moment(jsonLineItem.max_ship_time) : null,
    maxDeliveryTime: jsonLineItem.max_delivery_time ? moment(jsonLineItem.max_delivery_time) : null,
    hsCode: jsonLineItem.hs_code,
    variantTitle: jsonLineItem.variant_title,
    countryOfOrigin: jsonLineItem.country_of_origin
      ? countries[jsonLineItem.country_of_origin.toUpperCase()]
      : null,
  };
}

function toJSON(lineItem: LineItem): JSONLineItem {
  return {
    object_id: lineItem.objectId,
    id: lineItem.id,
    product: lineItem.product,
    variant: lineItem.variant,
    app_id: lineItem.appId,
    title: lineItem.title,
    sku: lineItem.sku,
    parcel: lineItem.parcel,
    quantity: lineItem.quantity,
    is_fulfilled: lineItem.isFulfilled,
    total_amount: lineItem.totalAmount,
    unit_amount: lineItem.unitAmount,
    currency: lineItem.currency ? lineItem.currency.iso : null,
    total_weight: lineItem.totalWeight,
    unit_weight: lineItem.unitWeight,
    weight_unit: lineItem.weightUnit,
    manufacture_country: lineItem.manufactureCountry,
    max_ship_time: lineItem.maxShipTime ? lineItem.maxShipTime.toISOString() : null,
    max_delivery_time: lineItem.maxDeliveryTime ? lineItem.maxDeliveryTime.toISOString() : null,
    hs_code: lineItem.hsCode,
    variant_title: lineItem.variantTitle,
    country_of_origin: lineItem.countryOfOrigin ? lineItem.countryOfOrigin.iso2 : null,
  };
}

function toJSONCustomsItemsPayload(lineItem: LineItem): JSONCustomsItemPayload {
  return {
    description: lineItem.title,
    unit_weight: lineItem.unitWeight,
    unit_value: lineItem.unitAmount,
    mass_unit: lineItem.weightUnit ? lineItem.weightUnit : '',
    value_currency: lineItem.currency ? lineItem.currency.iso : '',
    quantity: lineItem.quantity,
    origin_country: lineItem.countryOfOrigin?.iso2 || '',
    line_item: lineItem.id,
    sku_code: lineItem.sku,
    tariff_number: '',
    eccn_ear99: '',
    original_mass_unit: '',
    original_net_weight: '',
    original_unit_weight: '',
  };
}

export function fromWebLineItem(webLineItem: WebLineItem): LineItem {
  const currency =
    Object.values(currencies).find((currency) => currency.iso === webLineItem.currency) ||
    currencies.USD;
  const countryOfOrigin = countries[webLineItem.country_of_origin.toUpperCase()];
  return {
    objectId: webLineItem.object_id,
    variant: webLineItem.variant,
    title: webLineItem.title,
    sku: webLineItem.sku,
    quantity: webLineItem.quantity,
    isFulfilled: webLineItem.is_fulfilled,
    totalAmount: webLineItem.total_amount,
    unitAmount: webLineItem.unit_amount,
    totalWeight: webLineItem.total_weight,
    unitWeight: webLineItem.unit_weight,
    weightUnit: webLineItem.weight_unit,
    manufactureCountry: countryOfOrigin?.iso2,
    variantTitle: webLineItem.variant_title,
    countryOfOrigin,
    currency,

    //nulls
    id: -1,
    product: '',
    appId: '',
    parcel: undefined,
    maxShipTime: undefined,
    maxDeliveryTime: undefined,
    hsCode: undefined,
  };
}

function lineItemHasWeight(lineItem: LineItem): boolean {
  return (
    lineItem.weightUnit !== null &&
    lineItem.quantity !== 0 &&
    !!lineItem.totalWeight &&
    parseFloat(lineItem.totalWeight) !== 0
  );
}

function isLineItemValid(lineItem: LineItem): boolean {
  return [
    lineItem.quantity,
    lineItem.title,
    lineItem.weightUnit,
    lineItem.unitAmount,
    lineItem.unitWeight,
  ].every(Boolean);
}

function isCustomsReady(lineItem: LineItem): boolean {
  return isLineItemValid(lineItem) && !!lineItem.countryOfOrigin;
}

function payloadToJSON(lineItemPayload: LineItemPayload): JSONLineItemPayload {
  return {
    title: lineItemPayload.title,
    quantity: Number(lineItemPayload.quantity),
    unit_weight: lineItemPayload.unitWeight,
    weight_unit: lineItemPayload.weightUnit.value as string,
    unit_amount: lineItemPayload.unitAmount,
    currency: lineItemPayload.currency.value as string,
    country_of_origin: lineItemPayload.countryOfOrigin.iso2,
    sku: lineItemPayload.sku,
  };
}

function getTotalValue(total: number, lineItem: LineItem): number {
  const parsedValue = parseFloat(lineItem.totalAmount);
  return !isNaN(parsedValue) ? total + parsedValue : total;
}

function getTotalWeight(total: number, lineItem: LineItem): number {
  const parsedWeight = parseFloat(lineItem.totalWeight);
  return !isNaN(parsedWeight) ? total + parsedWeight : total;
}

function getLineItemsTotalValue(lineItems: LineItem[]): number {
  return lineItems.reduce(getTotalValue, 0.0);
}

function getLineItemsTotalWeight(linteItems: LineItem[]): number {
  return linteItems.reduce(getTotalWeight, 0.0);
}

function totalQuantity(lineItems: LineItem[]): number {
  return lineItems.reduce((tot, li) => tot + li.quantity, 0);
}

function sort(lineItems: LineItem[]): LineItem[] {
  return lineItems.sort((a, b) => {
    if (a.quantity === b.quantity) {
      return a.title.localeCompare(b.title);
    }

    return a.quantity > b.quantity ? -1 : 1;
  });
}

export default LineItem;
export {
  fromJSON,
  JSONLineItem,
  lineItemHasWeight,
  isLineItemValid,
  isCustomsReady,
  toJSON,
  payloadToJSON,
  LineItemPayload,
  JSONLineItemPayload,
  totalQuantity,
  getTotalValue,
  getTotalWeight,
  getLineItemsTotalValue,
  getLineItemsTotalWeight,
  toJSONCustomsItemsPayload,
  sort,
};
